import { Button, Modal } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import {
  wpSyncerService,
  isWpSyncerConflictError,
  extractConflictingRunId,
} from "../../../api/wpSyncer.service.js";
import styles from "./WpSyncer.module.css";

type TriggerSyncModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onTriggered: (runId: string) => void;
  onConflict: (existingRunId: string | null) => void;
};

export function TriggerSyncModal({
  isOpen,
  onClose,
  onTriggered,
  onConflict,
}: TriggerSyncModalProps) {
  const [force, setForce] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setForce(false);
    setDryRun(false);
    setSubmitting(false);
    setError(null);
  }, [isOpen]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await wpSyncerService.trigger({ force, dryRun });
      onTriggered(response.runId);
    } catch (err) {
      if (isWpSyncerConflictError(err)) {
        onConflict(extractConflictingRunId(err));
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Trigger WP Syncer" size="small" blurBackground>
      <div className={styles.modalBody}>
        {error && <div className={styles.errorBanner}>{error}</div>}

        <p>
          Runs the streaming MySQL → SQLite + sales-orders chain. Reads from the Mercury MySQL
          replica and writes to the legal500 Postgres database in-process during the mysql-to-sqlite
          stage; sales-orders runs after.
        </p>

        <div className={styles.formField}>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={force}
              onChange={event => setForce(event.target.checked)}
            />
            <span>
              <strong>Force re-download</strong> — ignore the cached snapshot and pull a fresh one
              from the source MySQL.
            </span>
          </label>
        </div>

        <div className={styles.formField}>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={dryRun}
              onChange={event => setDryRun(event.target.checked)}
            />
            <span>
              <strong>Dry run</strong> — read but do not write. Postgres is untouched and
              sales-orders is skipped.
            </span>
          </label>
        </div>

        <div className={styles.modalActions}>
          <Button color="default" variant="outlined" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Triggering…" : dryRun ? "Run dry-run" : "Trigger sync"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
